import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
import { Firebase } from '../firebase-loader.js';
import { Lobby } from './lobby.js';

/**
 * DISCORD PINGS, THE PLAYER'S SIDE (owner's plan, step 1, 27 Oct 2026).
 *
 * A signed-in player can ask for a Discord message when a listing of theirs
 * sells or their Moon Dream is ready. Turning pings on also joins the Moonfall
 * server, because a bot can only message members; the row says so before the
 * tap, and Discord's own screen says it again. The server holds the Discord
 * app (game/utils/discordPingLink.js); Convex holds the link and the settings
 * (convex/discordPings.ts).
 *
 * Two places offer it: a row (index.html, #discord-pings-row), which also
 * holds the switches and "Turn off", and one line where a wait starts (a new
 * listing, a new Moon Dream), offered only to a player who has no link yet.
 * Nothing shows where the server has no Discord app (MoonfallEnv.discordPings).
 *
 * The row sits in the Community sheet, under the Discord row. On a landscape
 * phone that sheet has no height to spare (Discord and the invitation fill
 * it), so there the row moves to a sheet of its own and a bell on the Discord
 * row opens it. The row is one box in every state; only its words and
 * switches change.
 */

let DiscordPings = {};

DiscordPings = (function () {
  const RETURN_OUTCOMES = Object.freeze({
    linked: ['discordPings.returnLinked', 'Discord pings are on.'],
    denied: ['discordPings.returnDenied', 'Discord pings stay off.'],
    failed: ['discordPings.returnFailed', 'Discord pings could not be turned on. Please try again.'],
  });
  const OFFERS = Object.freeze({
    sales: ['discordPings.offerSales', 'Get a Discord ping when it sells'],
    moonDream: ['discordPings.offerMoonDream', 'Get a Discord ping when it is ready'],
  });

  /* The hub's landscape-phone band, measured: at these heights the Community
     sheet scrolled with the row in it (tests/e2e-playwright/discord-pings.spec.js). */
  const OWN_SHEET_MEDIA = '(orientation: landscape) and (max-height: 500px)';
  let initialized = false;
  /** Null until read: { linked: false } or { linked, username, sales, moonDream, dmsClosed }. */
  let settings = null;
  let readInFlight = null;
  let busy = false;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    const template = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements).reduce(
      (text, [name, value]) => String(text).replaceAll(`{{${name}}}`, String(value)),
      String(template),
    );
  }

  function available() {
    return /** @type {any} */ (window).MoonfallEnv?.discordPings === true;
  }

  function toast(message) {
    /** @type {any} */
    const lobby = Lobby;
    lobby?.showToast?.(message);
  }

  function row() {
    return document.getElementById('discord-pings-row');
  }

  /** Where the row lives: the Community sheet, or its own sheet on a landscape phone. */
  function place() {
    const el = row();
    const community = document.getElementById('hub-community-body');
    const own = document.getElementById('hub-pings-body');
    const bell = document.getElementById('discord-pings-bell');
    if (!el || !community || !own) return;
    const inOwnSheet = window.matchMedia?.(OWN_SHEET_MEDIA)?.matches === true;
    if (bell) bell.hidden = !available() || !inOwnSheet;
    community.classList.toggle('has-pings-bell', Boolean(bell && !bell.hidden));
    // Never pull the row out of a sheet the player is looking at (a phone turned mid-read).
    if (el.closest('.hub-sheet.is-open')) return;
    if (inOwnSheet) {
      if (el.parentElement !== own) own.appendChild(el);
    } else if (el.parentElement !== community) {
      const discord = document.getElementById('pvp-discord-link');
      community.insertBefore(el, discord ? discord.nextSibling : community.firstChild);
    }
  }

  function rowState() {
    if (!settings) return 'reading';
    if (!settings.linked) return 'off';
    return settings.dmsClosed ? 'blocked' : 'on';
  }

  function render() {
    const el = row();
    if (!el) return;
    place();
    el.hidden = !available();
    if (el.hidden) return;
    const state = rowState();
    el.dataset.state = state;
    const bell = document.getElementById('discord-pings-bell');
    if (bell) bell.dataset.state = state;
    el.setAttribute('aria-busy', String(busy || state === 'reading'));
    // The fixed words carry data-i18n; only the name is filled here.
    const onTitle = el.querySelector('.discord-pings-title[data-when="on"]');
    if (onTitle) {
      onTitle.textContent = uiText('discordPings.onAs', 'Pings on, as {{name}}', { name: settings?.username || 'Discord' });
    }
    el.querySelectorAll('.discord-pings-kind').forEach((button) => {
      const kind = button.getAttribute('data-kind');
      button.setAttribute('aria-pressed', String(settings?.[kind] === true));
      /** @type {HTMLButtonElement} */ (button).disabled = busy;
    });
    const action = /** @type {HTMLButtonElement | null} */ (el.querySelector('.discord-pings-action'));
    if (action) action.disabled = busy || state === 'reading';
  }

  async function read() {
    if (!available() || !MoonfallCloud?.discordPings?.mine) return settings;
    if (readInFlight) return readInFlight;
    readInFlight = MoonfallCloud.discordPings.mine()
      .then((value) => {
        settings = value && typeof value === 'object' ? value : { linked: false };
        return settings;
      })
      .catch((err) => {
        console.warn('[DiscordPings] could not read the settings:', err?.message || err);
        return settings;
      })
      .finally(() => {
        readInFlight = null;
        render();
      });
    return readInFlight;
  }

  /** Off to Discord's own "allow" screen, in this tab: the server's callback brings the player back to /play. */
  async function turnOn() {
    if (busy || !available()) return;
    busy = true;
    render();
    try {
      const token = await Firebase.getIdToken();
      if (!token) throw new Error('not signed in');
      const res = await fetch('/api/discord/pings/start', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ language: i18n?.getLanguage?.() || 'en' }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || typeof data?.url !== 'string' || !data.url.startsWith('https://discord.com/')) {
        throw new Error(`start answered ${res.status}`);
      }
      window.location.assign(data.url);
    } catch (err) {
      console.warn('[DiscordPings] could not start:', err?.message || err);
      busy = false;
      render();
      toast(uiText(...RETURN_OUTCOMES.failed));
    }
  }

  async function turnOff() {
    if (busy) return;
    busy = true;
    render();
    try {
      await MoonfallCloud.discordPings.unlink();
      settings = { linked: false };
      toast(uiText('discordPings.turnedOff', 'Discord pings are off, and your Discord details are deleted.'));
    } catch (err) {
      console.warn('[DiscordPings] could not turn off:', err?.message || err);
      toast(uiText('discordPings.changeFailed', 'That did not save. Please try again.'));
    } finally {
      busy = false;
      render();
    }
  }

  async function toggleKind(kind) {
    if (busy || !settings?.linked || !(kind in OFFERS)) return;
    const previous = settings[kind] === true;
    settings = { ...settings, [kind]: !previous };
    busy = true;
    render();
    try {
      await MoonfallCloud.discordPings.set({ [kind]: !previous });
    } catch (err) {
      console.warn('[DiscordPings] could not change a ping:', err?.message || err);
      settings = { ...settings, [kind]: previous };
      toast(uiText('discordPings.changeFailed', 'That did not save. Please try again.'));
    } finally {
      busy = false;
      render();
    }
  }

  /** The return from Discord: say how it went once, then take the word out of the address. */
  function announceReturn() {
    const url = new URL(window.location.href);
    const outcome = url.searchParams.get('discord');
    if (!outcome) return;
    url.searchParams.delete('discord');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    const words = RETURN_OUTCOMES[outcome];
    if (words) toast(uiText(...words));
  }

  function bind() {
    const el = row();
    if (!el) return;
    el.querySelector('.discord-pings-action')?.addEventListener('click', () => {
      if (rowState() === 'on') void turnOff();
      else void turnOn();
    });
    el.querySelectorAll('.discord-pings-kind').forEach((button) => {
      button.addEventListener('click', () => void toggleKind(button.getAttribute('data-kind')));
    });
  }

  return {
    /** After sign-in: read the settings, so the sheet opens on its final words. */
    init() {
      if (initialized) return;
      initialized = true;
      bind();
      render();
      window.addEventListener('moonfall:languagechange', render);
      window.matchMedia?.(OWN_SHEET_MEDIA)?.addEventListener?.('change', place);
      announceReturn();
      void read();
    },

    /**
     * The one line where a wait starts, for a player with no link yet, or
     * null. A button styled as a link, ready to go after a status message.
     * @param {'sales'|'moonDream'} kind
     */
    offerFor(kind) {
      if (!available() || !(kind in OFFERS) || settings?.linked !== false) return null;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'discord-pings-offer';
      button.textContent = uiText(...OFFERS[kind]);
      button.addEventListener('click', () => void turnOn());
      return button;
    },

    refresh: read,
  };
})();

export { DiscordPings };
